import {useMemo} from 'react'

import {usePreferences} from '@/components/context/Preferences'
import {getCountryCards} from '@/content/countryCards'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {Jurisdiction} from '@/utils/countries/constants'
import {getJurisdictionsAlpha2} from '@/utils/countries/helpers'
import {useLanguage} from '@/utils/i18n/language'

const ALL_JURISDICTIONS = getJurisdictionsAlpha2()

export const useCountryCardsList = (
  numOfCountries: number,
  allowedJurisdictions: Jurisdiction[] = ALL_JURISDICTIONS,
) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {recommendedCountriesTitle, otherCountriesTitle} = getPricingContent().calculator
  const {origin, destination} = usePreferences()

  // Keeping this list referentially stable lets consumers memoize the per-country
  // pricing/tooltip content instead of recomputing it on every render - this list
  // never depends on the current selection, so it doesn't need to change when it does.
  return useMemo(() => {
    const regionalCountryCards = getCountryCards(
      language,
      origin,
      destination,
      numOfCountries,
      false,
      allowedJurisdictions,
    )
    const allCountryCards = getCountryCards(language, null, null, 0, true, allowedJurisdictions)

    return [
      {
        title: t(recommendedCountriesTitle),
        list: regionalCountryCards,
      },
      {
        title: t(otherCountriesTitle),
        list: allCountryCards,
        isSearchable: true,
      },
    ]
  }, [
    language,
    origin,
    destination,
    numOfCountries,
    allowedJurisdictions,
    t,
    recommendedCountriesTitle,
    otherCountriesTitle,
  ])
}
